import React, { useState } from 'react';
import './FAQ.css';
import PageHeader from '../components/PageHeader';
import { useBrandIdentity } from '../utils/brandIdentity';

function FAQ({ onNavigate }) {
  const { appName } = useBrandIdentity();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: `What is ${appName} and how does it work?`,
      answer: `${appName} is a risk assessment dashboard that analyzes trading strategy historical performance data. It calculates key risk metrics like maximum drawdown, average losses, and compares them against your account parameters (account size, number of contracts, max drawdown limits) to give you a clear risk assessment. Strategy data is updated daily with the latest performance results. Simply select a strategy, enter your account details, and get instant risk analysis.`
    },
    {
      question: "Where does the strategy data come from?",
      answer: `${appName} uses daily updated performance data for each trading strategy. The strategy data is maintained and automatically updated daily with the latest trading results. Historical daily profit and loss values are included for each strategy, allowing ${appName} to calculate risk metrics based on historical data. You simply select a strategy from the dropdown - the data is already there and ready to use.`
    },
    {
      question: "What's the difference between NQ and MNQ contract types?",
      answer: "NQ (Nasdaq E-mini) and MNQ (Micro E-mini Nasdaq) are different contract sizes. MNQ is 1/10th the value of NQ. Strategy data is typically based on NQ contracts. If you're trading MNQ, selecting MNQ will automatically adjust values by dividing by 10 so the analysis better reflects your selected position size."
    },
    {
      question: "How does the Account Blowout Risk calculation work?",
      answer: `The Account Blowout Risk compares your historical worst loss (scaled by your number of contracts) against your maximum trailing drawdown limit. If your worst historical loss exceeds your max drawdown, you'll see a 'NO GO' status because historically, this strategy has lost more than your account can handle. If it's below your limit, you'll see a 'GO' status with a buffer amount showing how much room you have. ⚠️ Important Note: Trailing drawdown is based on intraday drawdown movement, not end-of-day P&L. This means a trade could close positive but still blow the account if it exceeded the drawdown limit during the day. ${appName} uses end-of-day P&L data, so actual risk may be higher than shown. Always account for intraday volatility when assessing risk.`
    },
    {
      question: "What is the % Rule?",
      answer: "The % Rule is a consistency rule used by multiple prop firms. It limits how much one day can dominate your results relative to your selected percentage threshold."
    },
    {
      question: `How does ${appName} calculate the consistency limit?`,
      answer: `${appName} uses your Start-of-Day Profit Balance and Safety Net to determine a base amount (safety net when profit is lower, otherwise profit). It then applies your selected % (20/30/40/50/60) to compute the consistency limit and compares it with historical worst loss scaled by contracts.`
    },
    {
      question: "What does the Risk Score mean?",
      answer: "The Risk Score is a 0-100 scale that combines multiple risk factors. It considers your highest loss as a percentage of account size and your drawdown usage. Lower scores generally indicate lower historical risk based on the tool's methodology, while higher scores indicate greater historical risk. It is a quick summary metric, not a guarantee or universal measure of risk."
    },
    {
      question: "Can I analyze multiple accounts at once?",
      answer: `Yes! ${appName} Pro includes a Bulk Risk Calculator that lets you analyze up to 40 different account configurations simultaneously. You can compare different strategies, contract sizes, account sizes, and drawdown limits all at once. This is perfect for managing multiple prop firm accounts or testing different position sizes.`
    },
    {
      question: "Is my data secure?",
      answer: `Yes. ${appName} uses reasonable technical and administrative safeguards to protect your data. Depending on the feature you use, ${appName} may process and store account inputs, uploaded CSV data, synced results, and connected account data to provide the service. Payment details are handled by our billing provider, and we do not sell your personal information. See our Privacy Policy and Security page for more detail.`
    },
    {
      question: "How much historical data is needed for accurate risk assessment?",
      answer: `${appName} uses the available historical trading data for each strategy to calculate risk metrics. More trading days may improve the usefulness of the analysis. Strategies with more extensive historical data can provide more context for probability estimates and risk scores. The tool works with whatever historical data is available for each strategy.`
    },
    {
      question: "How accurate are the risk calculations?",
      answer: `${appName} calculates risk based on each strategy's historical performance data. It's important to understand that past performance doesn't guarantee future results. The tool shows you what has happened historically with each strategy, but market conditions can change. Use ${appName} as one tool in your risk management toolkit, not as the sole determinant of your trading decisions. Always consider current market conditions and consult with financial professionals when making significant trading decisions.`
    },
    {
      question: "What's the difference between 'Risk' mode and '% Rule' mode?",
      answer: "Risk mode focuses on your account's maximum trailing drawdown limit - it tells you if historical losses could blow your account based on drawdown thresholds. % Rule mode focuses on consistency checks - it calculates your consistency limit from your selected percentage and compares it to historical losses. Both are important, but they measure different types of risk."
    },
    {
      question: `Can I use ${appName} for live trading decisions?`,
      answer: `${appName} is designed to help you assess risk before you start trading a strategy. It analyzes historical data to give you a risk profile. However, it doesn't track live positions or real-time P&L. Use ${appName} to evaluate strategies and account setups, but always monitor your live positions separately and follow your broker's or prop firm's real-time risk management tools.`
    },
    {
      question: "How often is the strategy data updated?",
      answer: "Strategy performance data is updated daily with the latest trading results. This helps keep your risk assessments based on the latest data available in the system at the time of sync. Each strategy's historical daily profit and loss values are refreshed automatically, so you have access to updated risk analysis."
    },
    {
      question: "Can I add my own strategy or update the data?",
      answer: `The strategy data is maintained by ${appName}'s development team and updated automatically. If you'd like to request a new strategy to be added or have questions about the available strategies, please contact support. The daily updates ensure all strategies reflect the latest trading performance.`
    },
    {
      question: "What if a strategy I want to analyze isn't in the list?",
      answer: `If you don't see a specific strategy in the dropdown, it may not be available yet. Strategy data is added and maintained by ${appName}'s team. You can contact support to request that a strategy be added. In the meantime, you can use similar strategies that are available to get a sense of risk levels.`
    },
    {
      question: `Is ${appName} affiliated with Apex Trader Funding?`,
      answer: `${appName} is an independent analytics product and is not a financial advisory service. ${appName} is not affiliated with Apex Trader Funding or other trading platforms. See our disclaimers for details on responsibility and use.`
    }
  ];

  return (
    <>
      <PageHeader onNavigate={onNavigate} />
      <div className="faq-page">
      <div className="faq-container">
        <h1 className="faq-title">Frequently Asked Questions</h1>
        <p className="faq-intro">
          Everything you need to know about {appName} and how it helps you manage trading risk.
        </p>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
              <button
                className="faq-question"
                onClick={() => toggleQuestion(index)}
              >
                <span className="faq-question-text">{faq.question}</span>
                <span className="faq-toggle-icon">{openIndex === index ? '−' : '+'}</span>
              </button>
              {openIndex === index && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}

export default FAQ;

